/*
 * Card review counts from Yotpo's own summary (brief P1-7, 2026-10-17).
 *
 * Cards draw the count from the review metafields Yotpo syncs about once a day
 * (blocks/ra-collection-card-reviews.liquid); product pages show Yotpo's live figure (its widget, and
 * assets/ra-reviews-yotpo.js for the rating under the title). So a card read 164 while its page read 165.
 * Now both read Yotpo's summary: each card rating that comes near the screen is refreshed from it, one
 * small cached request per product (shared by every card of that product, and kept for the visit), after
 * the page has drawn. Until then, or if Yotpo does not answer, the stored count shows as before.
 * Same consent rule as the product page refresh: only when Shopify's consent allows analytics.
 */
(() => {
  const root = document.querySelector('[data-ra-yotpo-key]');
  const key = root && root.getAttribute('data-ra-yotpo-key');
  if (!key || !('IntersectionObserver' in window)) return;
  const privacy = window.Shopify && window.Shopify.customerPrivacy;
  if (privacy && privacy.analyticsProcessingAllowed && !privacy.analyticsProcessingAllowed()) return;

  const TTL = 3600000;
  const asked = new Map();
  const summary = (id) => {
    if (asked.has(id)) return asked.get(id);
    let cached = null;
    try {
      cached = JSON.parse(sessionStorage.getItem(`ra-yb:${id}`) || 'null');
    } catch {
      cached = null;
    }
    const job =
      cached && Date.now() - cached.t < TTL
        ? Promise.resolve(cached)
        : fetch(`https://api-cdn.yotpo.com/v1/widget/${key}/products/${id}/reviews.json?per_page=1`)
            .then((response) => (response.ok ? response.json() : null))
            .then((data) => {
              const line = data && data.response && data.response.bottomline;
              if (!line) return null;
              const out = { c: Number(line.total_review), a: Number(line.average_score), t: Date.now() };
              try {
                sessionStorage.setItem(`ra-yb:${id}`, JSON.stringify(out));
              } catch {
                /* private mode: no cache */
              }
              return out;
            })
            .catch(() => null);
    asked.set(id, job);
    return job;
  };

  const refresh = async (card) => {
    const line = await summary(card.getAttribute('data-product-id') || '');
    if (!line || !(line.c > 0) || !(line.a > 0)) return;
    const count = card.querySelector('[data-ra-review-count]');
    if (count) count.textContent = line.c.toLocaleString('en-US');
    /* one decimal, rounded down, as the reviews widget and the product page show it */
    const score = line.a >= 5 ? '5.0' : (Math.floor(line.a * 10) / 10).toFixed(1);
    const label = card.querySelector('.visually-hidden');
    if (label) label.textContent = `Rated ${score} out of 5 stars.`;
    const stars = card.querySelector('.ra-card-reviews__stars');
    if (stars) stars.style.setProperty('--ra-stars-fill', `${Math.min(100, line.a * 20)}%`);
  };

  const near = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        near.unobserve(entry.target);
        refresh(entry.target);
      }
    },
    { rootMargin: '300px' }
  );
  const watch = () => {
    for (const card of document.querySelectorAll('[data-ra-review-summary][data-product-id]:not([data-ra-watched])')) {
      card.setAttribute('data-ra-watched', '');
      near.observe(card);
    }
  };
  watch();
  /* cards added later (infinite scroll, recommendations) */
  let later = 0;
  new MutationObserver(() => {
    clearTimeout(later);
    later = setTimeout(watch, 300);
  }).observe(document.body, { childList: true, subtree: true });
})();
